import React from 'react';
import { HelpDot } from '../HelpDot';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { DataPagination } from '../ui/data-pagination';
import { Edit, Trash2, Phone, Mail, Lock, Loader2, Target, Activity, X, Users, UserCheck } from 'lucide-react';
import { formatLocalDate } from '../../lib/dateUtils';

export function LeadTableView({
  sortedLeads,
  sortBy,
  setSortBy,
  pipelineStages,
  statusConfigs,
  teamMembers,
  currency,
  currentPage,
  totalCount,
  pageSize,
  setCurrentPage,
  setPageSize,
  hasPermission,
  handleEdit,
  handleDelete,
  handleRevealContact,
  revealingLeadId,
  selectedLeadIds,
  toggleSelectLead,
  toggleSelectAll,
  clearSelection,
  onBulkDeleteClick,
  onBulkMoveAudienceClick,
  onBulkAssignClick,
  selectAllMatching,
  onSelectAllMatching,
}) {
  const canDelete = hasPermission('lead', 'kanban', 'delete');
  const canMove = hasPermission('lead', 'kanban', 'update');
  const pageIds = sortedLeads.map((l) => l.id);
  const selectedCount = selectedLeadIds ? selectedLeadIds.size : 0;
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedLeadIds?.has(id));
  const somePageSelected = pageIds.some((id) => selectedLeadIds?.has(id));
  const effectiveCount = selectAllMatching ? totalCount : selectedCount;
  const canSelectAllMatching = allPageSelected && !selectAllMatching && totalCount > selectedCount;

  return (
    <Card data-testid="leads-table">
      {(canDelete || canMove) && selectedCount > 0 && (
        <div
          className="flex flex-col gap-2 px-4 py-2.5 border-b bg-muted/40"
          data-testid="bulk-action-bar"
        >
         <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium" data-testid="bulk-selected-count">
              {selectAllMatching ? `All ${totalCount} selected` : `${selectedCount} selected`}
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="bulk-clear-selection"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canMove && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkAssignClick}
                data-testid="bulk-assign-btn"
                title="Assign the selected leads to a teammate (or split across teammates)"
              >
                <UserCheck className="h-4 w-4 mr-1.5" /> Assign
              </Button>
            )}
            {canMove && (
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkMoveAudienceClick}
                data-testid="bulk-move-audience-btn"
                title="Move selected leads off the pipeline into the cold Audience list (stops any active outreach)"
              >
                <Users className="h-4 w-4 mr-1.5" /> Move to Audience
              </Button>
            )}
            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={onBulkDeleteClick}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete selected
              </Button>
            )}
          </div>
         </div>
         {(canSelectAllMatching || selectAllMatching) && (
           <div className="text-xs text-muted-foreground" data-testid="select-all-matching-banner">
             {selectAllMatching ? (
               <>All <b>{totalCount}</b> leads matching this view are selected. </>
             ) : (
               <>All {selectedCount} on this page are selected. </>
             )}
             <button
               type="button"
               className="text-primary underline underline-offset-2 hover:opacity-80"
               onClick={() => onSelectAllMatching?.(!selectAllMatching)}
               data-testid="select-all-matching-toggle"
             >
               {selectAllMatching ? 'Clear selection' : `Select all ${totalCount} matching`}
             </button>
           </div>
         )}
        </div>
      )}
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/30">
                {canDelete && (
                  <th className="w-10 p-3 text-left">
                    <Checkbox
                      checked={allPageSelected ? true : (somePageSelected ? 'indeterminate' : false)}
                      onCheckedChange={() => toggleSelectAll(pageIds)}
                      aria-label="Select all leads on this page"
                      data-testid="select-all-leads"
                    />
                  </th>
                )}
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Name</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Company</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                  <span className="inline-flex items-center gap-1">Email <HelpDot guide="reveal-and-reports" label="How revealing contacts works" /></span>
                </th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Phone</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Assigned To</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Product/Service</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                  <button
                    type="button"
                    data-testid="leads-sort-created"
                    onClick={() => setSortBy && setSortBy(sortBy === 'created_desc' ? 'created_asc' : 'created_desc')}
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    title="Sort by created date"
                  >
                    Created
                    <span className="text-[10px]">{sortBy === 'created_asc' ? '▲' : sortBy === 'created_desc' ? '▼' : '↕'}</span>
                  </button>
                </th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Value</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                  <span className="inline-flex items-center gap-1">AI Score <HelpDot guide="build-pipeline" label="How AI Score works" /></span>
                </th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">ICP Fit</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Engagement</th>
                <th className="text-left p-3 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeads.length === 0 ? (
                <tr>
                  <td colSpan={canDelete ? 14 : 13} className="text-center py-8 text-sm text-muted-foreground">
                    No leads found
                  </td>
                </tr>
              ) : (
                sortedLeads.map((lead) => {
                  const statusObj = pipelineStages.find(s => s.name.toLowerCase() === lead.status?.toLowerCase());
                  const isSelected = selectedLeadIds?.has(lead.id);
                  return (
                    <tr key={lead.id} className={`border-b hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`} data-testid={`lead-row-${lead.id}`}>
                      {canDelete && (
                        <td className="w-10 p-3">
                          <Checkbox
                            checked={!!isSelected}
                            onCheckedChange={() => toggleSelectLead(lead.id)}
                            aria-label={`Select ${lead.name || 'lead'}`}
                            data-testid={`select-lead-${lead.id}`}
                          />
                        </td>
                      )}
                      <td className="p-3 text-sm font-medium">{lead.name}</td>
                      <td className="p-3 text-sm text-muted-foreground">{lead.company || '-'}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {lead.email ? (
                          <span>{lead.email}</span>
                        ) : (lead.apollo_id || lead.provider_id || (lead.source || '').toLowerCase().includes('apollo')) && !lead.contact_revealed ? (
                          <button 
                            onClick={() => handleRevealContact(lead)}
                            disabled={revealingLeadId === lead.id}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 hover:text-amber-700 transition-colors"
                            data-testid={`reveal-email-btn-${lead.id}`}
                          >
                            {revealingLeadId === lead.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Lock className="h-3 w-3" />
                            )}
                            Reveal
                          </button>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {lead.phone ? (
                          <span>{lead.phone}</span>
                        ) : (lead.apollo_id || lead.provider_id || (lead.source || '').toLowerCase().includes('apollo')) && !lead.contact_revealed ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/50">
                            <Lock className="h-3 w-3" /> Locked
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-3">
                        <Badge
                          className="text-xs"
                          style={{
                            backgroundColor: statusObj?.color || statusConfigs[lead.status]?.bgColor || '#64748b',
                            color: '#ffffff',
                            border: 'none'
                          }}
                        >
                          {statusObj?.name || statusConfigs[lead.status]?.label || lead.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {lead.assigned_to ? 
                          teamMembers.find(m => m.id === lead.assigned_to)?.name || 'Unknown' : 
                          'Unassigned'
                        }
                      </td>
                      <td className="p-3 text-sm text-muted-foreground" data-testid={`lead-product-${lead.id}`}>
                        {lead.interested_product_name || lead.recommended_product_name || '-'}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground whitespace-nowrap" data-testid={`lead-created-${lead.id}`}>
                        {lead.created_at ? formatLocalDate(lead.created_at) : '-'}
                      </td>
                      <td className="p-3 text-sm font-medium">{currency.format(parseFloat(lead.value || 0))}</td>
                      <td className="p-3">
                        {lead.ai_score != null ? (
                          <div className="flex items-center gap-1.5" data-testid={`lead-ai-score-${lead.id}`}>
                            <div className="relative w-7 h-7">
                              <svg className="w-7 h-7 -rotate-90" viewBox="0 0 36 36">
                                <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                                <circle cx="18" cy="18" r="15.5" fill="none"
                                  stroke={lead.ai_score >= 70 ? '#ef4444' : lead.ai_score >= 40 ? '#f59e0b' : '#3b82f6'}
                                  strokeWidth="3" strokeDasharray={`${lead.ai_score} ${100 - lead.ai_score}`} strokeLinecap="round" />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold">{lead.ai_score}</span>
                            </div>
                            <span className={`text-[10px] font-medium uppercase ${
                              lead.ai_qualification === 'hot' ? 'text-red-600' :
                              lead.ai_qualification === 'warm' ? 'text-amber-600' :
                              lead.ai_qualification === 'cold' ? 'text-blue-600' : 'text-muted-foreground'
                            }`}>{lead.ai_qualification || ''}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="p-3">
                        {lead.ai_icp_match && lead.ai_icp_match.score != null ? (
                          <div className="flex items-center gap-1.5" data-testid={`table-icp-fit-${lead.id}`}>
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                lead.ai_icp_match.score >= 80 ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' :
                                lead.ai_icp_match.score >= 50 ? 'bg-amber-50 text-amber-600 border border-amber-200' :
                                'bg-red-50 text-red-500 border border-red-200'
                              }`}
                              title={lead.ai_icp_match.matching_factors ? lead.ai_icp_match.matching_factors.join(', ') : ''}
                            >
                              <Target className="h-2.5 w-2.5" />
                              {lead.ai_icp_match.score}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="p-3">
                        {lead.engagement_score != null ? (
                          <div className="flex items-center gap-1.5" data-testid={`table-engagement-${lead.id}`}>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              lead.engagement_level === 'hot' ? 'bg-orange-50 text-orange-600 border border-orange-200' :
                              lead.engagement_level === 'warm' ? 'bg-yellow-50 text-yellow-600 border border-yellow-200' :
                              'bg-sky-50 text-sky-600 border border-sky-200'
                            }`}>
                              <Activity className="h-2.5 w-2.5" />
                              {lead.engagement_level === 'hot' ? 'Hot' : lead.engagement_level === 'warm' ? 'Warm' : 'Cold'} · {lead.engagement_score}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {hasPermission('lead', 'kanban', 'update') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(lead)}
                              data-testid={`table-edit-lead-${lead.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {hasPermission('lead', 'kanban', 'delete') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(lead.id)}
                              data-testid={`table-delete-lead-${lead.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        <DataPagination
          currentPage={currentPage}
          totalItems={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setCurrentPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          className="px-4"
        />
      </CardContent>
    </Card>
  );
}
